import React, { useState, useRef, useImperativeHandle, forwardRef } from "react";
import { Stage, Layer, Rect, Ellipse, Arrow, Text, Line } from "react-konva";
import {
  MousePointer2, Square, Circle, ArrowUpRight, Type, Pencil, Trash2, Undo2, Eraser,
} from "lucide-react";

const CANVAS_WIDTH = 720;
const CANVAS_HEIGHT = 400;
const STROKE_COLOR = "#4338CA";
const TEXT_COLOR = "#1E293B";

const TOOLS = [
  { id: "select", icon: MousePointer2, label: "Select / Move" },
  { id: "rect", icon: Square, label: "Component Box" },
  { id: "ellipse", icon: Circle, label: "DB / Node" },
  { id: "arrow", icon: ArrowUpRight, label: "Connector" },
  { id: "text", icon: Type, label: "Label" },
  { id: "pen", icon: Pencil, label: "Freehand" },
];

const WhiteboardCanvas = forwardRef(function WhiteboardCanvas({ shapes, onChange }, ref) {
  const [tool, setTool] = useState("select");
  const [selectedId, setSelectedId] = useState(null);
  const drawingRef = useRef(null);
  const historyRef = useRef([]);
  const stageRef = useRef(null);

  useImperativeHandle(ref, () => ({
    exportImage: () => {
      const stage = stageRef.current;
      if (!stage) return null;
      const hasShapes = (shapes || []).length > 0;
      if (!hasShapes) return null;
      return stage.toDataURL({ pixelRatio: 1.5 }).split(",")[1];
    },
    isEmpty: () => (shapes || []).length === 0,
  }));

  const pushHistory = () => {
    historyRef.current.push(shapes || []);
    if (historyRef.current.length > 30) historyRef.current.shift();
  };

  const setShapes = (next) => onChange(next);

  const handleUndo = () => {
    const prev = historyRef.current.pop();
    if (prev !== undefined) setShapes(prev);
  };

  const handleClear = () => {
    pushHistory();
    setShapes([]);
    setSelectedId(null);
  };

  const handleDeleteSelected = () => {
    if (!selectedId) return;
    pushHistory();
    setShapes((shapes || []).filter((s) => s.id !== selectedId));
    setSelectedId(null);
  };

  const genId = () => `sh_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

  const handleStageMouseDown = (e) => {
    const stage = stageRef.current;
    const pos = stage.getPointerPosition();
    const clickedOnEmpty = e.target === stage;

    if (tool === "select") {
      if (clickedOnEmpty) setSelectedId(null);
      return;
    }

    pushHistory();

    if (tool === "text") {
      const value = window.prompt("Label text:", "");
      if (value && value.trim()) {
        setShapes([...(shapes || []), { id: genId(), type: "text", x: pos.x, y: pos.y, text: value.trim() }]);
      }
      return;
    }

    if (tool === "pen") {
      drawingRef.current = { id: genId(), type: "line", points: [pos.x, pos.y] };
      setShapes([...(shapes || []), drawingRef.current]);
      return;
    }

    if (tool === "rect") {
      drawingRef.current = { id: genId(), type: "rect", x: pos.x, y: pos.y, width: 0, height: 0 };
      setShapes([...(shapes || []), drawingRef.current]);
      return;
    }

    if (tool === "ellipse") {
      drawingRef.current = { id: genId(), type: "ellipse", x: pos.x, y: pos.y, radiusX: 0, radiusY: 0 };
      setShapes([...(shapes || []), drawingRef.current]);
      return;
    }

    if (tool === "arrow") {
      drawingRef.current = { id: genId(), type: "arrow", points: [pos.x, pos.y, pos.x, pos.y] };
      setShapes([...(shapes || []), drawingRef.current]);
      return;
    }
  };

  const handleStageMouseMove = () => {
    if (!drawingRef.current) return;
    const stage = stageRef.current;
    const pos = stage.getPointerPosition();
    const current = drawingRef.current;
    const list = shapes || [];
    let next;

    if (current.type === "line") {
      next = list.map((s) => (s.id === current.id ? { ...s, points: [...s.points, pos.x, pos.y] } : s));
    } else if (current.type === "rect") {
      next = list.map((s) => (s.id === current.id ? { ...s, width: pos.x - s.x, height: pos.y - s.y } : s));
    } else if (current.type === "ellipse") {
      next = list.map((s) => (s.id === current.id ? { ...s, radiusX: Math.abs(pos.x - s.x), radiusY: Math.abs(pos.y - s.y) } : s));
    } else if (current.type === "arrow") {
      next = list.map((s) => (s.id === current.id ? { ...s, points: [s.points[0], s.points[1], pos.x, pos.y] } : s));
    }
    if (next) setShapes(next);
  };

  const handleStageMouseUp = () => {
    drawingRef.current = null;
  };

  const renderShape = (s) => {
    const common = {
      key: s.id,
      draggable: tool === "select",
      onClick: () => tool === "select" && setSelectedId(s.id),
      onTap: () => tool === "select" && setSelectedId(s.id),
      onDragEnd: (e) => {
        const { x, y } = e.target.position();
        setShapes((shapes || []).map((sh) => (sh.id === s.id ? { ...sh, x, y } : sh)));
      },
      stroke: s.id === selectedId ? "#F59E0B" : STROKE_COLOR,
      strokeWidth: s.id === selectedId ? 2.5 : 2,
    };
    if (s.type === "rect") {
      return <Rect {...common} x={s.x} y={s.y} width={s.width} height={s.height} fill="rgba(99,102,241,0.06)" cornerRadius={6} />;
    }
    if (s.type === "ellipse") {
      return <Ellipse {...common} x={s.x} y={s.y} radiusX={s.radiusX} radiusY={s.radiusY} fill="rgba(99,102,241,0.06)" />;
    }
    if (s.type === "arrow") {
      return <Arrow {...common} points={s.points} pointerLength={10} pointerWidth={10} fill={common.stroke} draggable={false} />;
    }
    if (s.type === "line") {
      return <Line {...common} points={s.points} tension={0.4} lineCap="round" draggable={false} />;
    }
    if (s.type === "text") {
      return <Text {...common} x={s.x} y={s.y} text={s.text} fontSize={15} fill={s.id === selectedId ? "#F59E0B" : TEXT_COLOR} fontStyle="bold" stroke={undefined} strokeWidth={undefined} />;
    }
    return null;
  };

  return (
    <div data-testid="whiteboard-canvas-container">
      <div className="flex items-center gap-1.5 flex-wrap mb-2.5 bg-white/[0.03] border border-white/10 rounded-xl p-1.5">
        {TOOLS.map((t) => {
          const Icon = t.icon;
          const active = tool === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => { setTool(t.id); setSelectedId(null); }}
              title={t.label}
              data-testid={`whiteboard-tool-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                active ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
        <div className="w-px h-5 bg-white/10 mx-1" />
        <button type="button" onClick={handleDeleteSelected} disabled={!selectedId} data-testid="whiteboard-delete-selected-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-400 hover:text-rose-400 disabled:opacity-30 disabled:cursor-not-allowed">
          <Eraser className="w-3.5 h-3.5" /> Delete
        </button>
        <button type="button" onClick={handleUndo} data-testid="whiteboard-undo-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-400 hover:text-white">
          <Undo2 className="w-3.5 h-3.5" /> Undo
        </button>
        <button type="button" onClick={handleClear} data-testid="whiteboard-clear-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-400 hover:text-rose-400">
          <Trash2 className="w-3.5 h-3.5" /> Clear All
        </button>
      </div>

      <div className="rounded-xl overflow-hidden border border-white/10" style={{ background: "#F8FAFC" }} data-testid="whiteboard-stage-wrapper">
        <Stage
          ref={stageRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          onMouseDown={handleStageMouseDown}
          onMousemove={handleStageMouseMove}
          onMouseup={handleStageMouseUp}
          onMouseMove={handleStageMouseMove}
          onMouseUp={handleStageMouseUp}
          data-testid="whiteboard-stage"
        >
          <Layer>{(shapes || []).map(renderShape)}</Layer>
        </Stage>
      </div>
      <p className="text-[11px] text-slate-500 mt-1.5">
        Draw your system design here — boxes for components, arrows for data flow. Our AI reviews it just like a real interviewer looking at a whiteboard.
      </p>
    </div>
  );
});

export default WhiteboardCanvas;
